import {AfterViewChecked, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {catchError, filter, forkJoin, interval, map, merge, Observable, of, Subscription, switchMap, tap} from 'rxjs';
import {PersonUtils} from 'src/app/auth_profile/utils/v3/person-utils';
import {Dates, TimeUnits} from 'src/app/utils/calendar-utils';
import {environment} from 'src/environments/environment';
import {NativeServiceApiProvider} from '../../../../services/native-api-provider.service';
import {Pageable} from '../../../../utils/pageable';
import {Utils} from '../../../utils/lesson-utils';
import {DomSanitizer, SafeHtml, SafeResourceUrl} from "@angular/platform-browser";
import {ModalComponent} from "../../../../utils/modal/modal.component";
import {WebSocketService} from 'src/app/col/services/web-socket.service';
import {TranslateService} from "@ngx-translate/core";
import * as LessonsV3 from '../../../model/v3/lessons';
import {AuthorizationServiceProvider} from "../../../../auth_profile/services/authorization-service.provider";
import {CourseProductMapper} from "../../../utils/course-product-mapper";

declare var bootstrap: any;

@Component({
    selector: 'app-student-lesson-details',
    templateUrl: './student-lesson-details.component.html',
    styleUrls: ['./student-lesson-details.component.scss'],
    standalone: false
})
export class StudentLessonDetailsComponent implements OnInit, OnDestroy, AfterViewChecked {
  READING_RECOMENDATION = 22;
  REVISION_LESSONS = 8;
  TOOLTIP_TIME = 3000;
  readonly LessonStatus = LessonsV3.LessonStatus;
  readonly LessonType = LessonsV3.LessonType;

  private _studentId: number;
  _lesson: LessonsV3.LFStudentGeneral;
  private schoolId: number;
  lessonFlags: LessonsV3.ApiLessonFlag[] = [];
  lessonMessage: LessonsV3.ApiLessonMessage | null = null;
  lessonTeacher: LessonsV3.Teacher | null = null;
  lessonCourseName = '';
  lessonCancellationDetails: LessonsV3.ApiLessonCancellationDetails | null = null;
  lessonRoomUrlRaw: string | null = null;
  committedProgress: LessonsV3.LessonProgressDetails | null = null;
  lessonTeacherPhotoUrl: string | null = null;
  _isNext: boolean;
  progress: LessonsV3.LessonProgressDetails | null = null;
  timePhrase: string;
  timeSubscription: Subscription;
  wsSubscription: Subscription;
  translateSubscription: Subscription;
  authSubscription: Subscription;
  enrichLessonSubscription: Subscription | null = null;

  isSkype: boolean;
  startedAt: Date = null;
  roomUrl: SafeResourceUrl;
  plannedAt: Date;
  isComing: boolean;
  isStarted: boolean;
  shouldStart: boolean;
  mayStart: boolean;
  isAfterPlannedStartDate: boolean;
  htmlContent: SafeHtml;
  tooltipCopyText: string;
  tooltipCopiedText: string;
  isCopied = false;
  isTooltipPrepared: boolean = false;
  cancelable: boolean = false;
  cancelReason: string;
  readonly cancelReasonMaxLength = 250;
  @ViewChild('cancelLessonModal', { static: true })
  cancelLessonModal: ModalComponent;
  private teacherSkype: string;
  @ViewChild("classroomFallback", {static: true})
  classroomFallbackModal: ModalComponent;
  @Input() set studentId(id: number) {
    this._studentId = id;
    this.listenToWebSocket();
  }

  @Input() set lesson(lesson: LessonsV3.LFStudentGeneral) {
    this._lesson = lesson;
    this.enrichLessonFromV3Apis();
    this.loadLessonDetails();
    this.listenToWebSocket();
  }

  isWebMSupported = false;
  provaVideoUrl: SafeResourceUrl;
  provaVideoYouTubeUrl = 'https://www.youtube.com/watch?v=NHuDELsXsJE';

  constructor(
    private router: Router,
    private nativeServiceApiProvider: NativeServiceApiProvider,
    private authService: AuthorizationServiceProvider,
    private sanitizer: DomSanitizer,
    private webSocket: WebSocketService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.timeSubscription = interval(1000).pipe(
      tap( () => this.prepareDetails()),
      map(() => this.prepareTimePhrase())
    ).subscribe( phrase => this.timePhrase = phrase);
    this.translateSubscription = this.translateService.onLangChange.subscribe(
      _ => this.getTranslations()
    );
    this.getTranslations();
    this.authSubscription = this.authService.getAuthDetailsService().pipe(
      switchMap((api) => api.getSelfSchoolId()),
      tap((schoolId) => {
        this.schoolId = schoolId;
      })
    ).subscribe();
    this.getProvaVideoUrl();
    const video = document.createElement('video');
    this.isWebMSupported = !!video.canPlayType && video.canPlayType('video/webm; codecs="vp8, vorbis"') !== '';
  }

  ngAfterViewChecked(): void {
    if (!this.isTooltipPrepared) this.prepareTooltips()
  }

  ngOnDestroy(): void {
    this.timeSubscription.unsubscribe();
    this.wsSubscription?.unsubscribe();
    this.translateSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
    this.enrichLessonSubscription?.unsubscribe();
  }

  getTeacherName() {
    return this.lessonTeacher ? PersonUtils.getPersonName(this.lessonTeacher.person) : '';
  }

  getInitials() {
    return this.lessonTeacher ? PersonUtils.getInitials(this.lessonTeacher.person) : '';
  }

  getLessonDate() {
    return this._lesson?.lessonMetric?.plannedStartDate || null;
  }

  getCourseName() {
    return this.lessonCourseName;
  }

  isProva() {
    return this._lesson && this._lesson.type === LessonsV3.LessonType.Prova;
  }

  getCourseLangName() {
    return (
      (this._lesson &&
        ((this.isSpanish() && 'COL.COURSES.SP') ||
          (this.isBusiness() && 'COL.COURSES.ENBUS') ||
          (this.isKidsEnglish() && 'COL.COURSES.ENCFK') ||
          (this.isEnglishCore() && 'COL.COURSES.EN'))) ||
      ''
    );
  }

  isEnglishCore() {
    return this.getLessonCourseCode()?.startsWith('en.') ?? false;
  }

  isSpanish() {
    return this.getLessonCourseCode()?.startsWith('sp.') ?? false;
  }

  isBusiness() {
    return this.getLessonCourseCode()?.startsWith('en.bs.') ?? false;
  }

  isKidsEnglish() {
    return this.getLessonCourseCode()?.startsWith('en.ch.') ?? false;
  }

  listenToWebSocket(): void {
    if (!this._studentId || this.wsSubscription) return
    this.wsSubscription = this.webSocket.establish(environment.eventsWsEndpoint).pipe(
      filter(connection => connection?.client?.connected),
      switchMap(connection => merge(
        connection.subscribe(`/col/student/${this._studentId}/LessonListUpdate`),
        connection.subscribe(`/col/student/${this._studentId}/LessonUpdate`)
      )),
    ).subscribe();
  }

  loadLessonDetails(): void {
    this.checkIfIsTheLatest();
    this.prepareDetails();
  }

  private enrichLessonFromV3Apis(): void {
    if (!this._lesson?.id) {
      return;
    }
    this.enrichLessonSubscription?.unsubscribe();
    this.enrichLessonSubscription = this.nativeServiceApiProvider.colV3().pipe(
      switchMap((api) =>
        this.resolveSchoolId().pipe(
          switchMap((schoolId) =>
            forkJoin({
              lesson: api.getLesson(this._lesson.id, 'student-full', this._studentId).pipe(
                map((lesson) => lesson as LessonsV3.LFStudentGeneral)
              ),
              lessonProgress: api.getLessonProgress(this._lesson.id),
              flags: api.getLessonFlags(this._lesson.id),
              message: this._studentId ? api.getLessonComment(this._lesson.id, this._studentId) : of(null)
            }).pipe(
              switchMap(({lesson, lessonProgress, flags, message}) => {
                const courseCode = this.getLessonCourseCode(lesson);
                const productCode = this.getProductCodeFromCourseCode(courseCode);
                if (!schoolId || !courseCode || !productCode) {
                  return of({lesson, lessonProgress, flags, message, courseName: ''});
                }
                return api.getProductCourses(schoolId, productCode).pipe(
                  map((courses) => ({
                    lesson,
                    lessonProgress,
                    flags,
                    message,
                    courseName: courses?.find((course) => course.code === courseCode)?.name ?? ''
                  }))
                );
              })
            )
          )
        )
      ),
    ).subscribe(({lesson, lessonProgress, flags, message, courseName}) => {
      this._lesson = lesson;
      this.lessonFlags = flags ?? [];
      this.lessonMessage = message ?? null;
      this.lessonTeacher = this.getLessonTeacherDetails(lesson);
      this.loadLessonTeacherPhoto();
      this.lessonCancellationDetails = this.getLessonCancellationDetails(lesson);
      this.lessonRoomUrlRaw = this.getLessonRoomUrl(lesson);
      this.committedProgress = lessonProgress ?? null;
      this.lessonCourseName = courseName;
      this.prepareDetails();
    });
  }

  private resolveSchoolId(): Observable<number | null> {
    if (this.schoolId) {
      return of(this.schoolId);
    }
    return this.authService.getAuthDetailsService().pipe(
      switchMap((api) => api.getSelfSchoolId()),
      tap((schoolId) => {
        this.schoolId = schoolId;
      }),
      map((schoolId) => schoolId ?? null)
    );
  }

  checkIfIsTheLatest(): void {
    this.nativeServiceApiProvider
      .colV3()
      .pipe(
        switchMap((api) =>
          api.findLessons(
            LessonsV3.LessonCategory.Col,
            null,
            'student-preview',
            null,
            this._studentId,
            null,
            null,
            null,
            null,
            [LessonsV3.LessonStatus.Due, LessonsV3.LessonStatus.Booked],
            new Date(),
            null,
            new Pageable(0, 1, ['metric.details.plannedStartDate,ASC'])
          )
        ),
        map((page) => page?.content?.[0] ?? null)
      )
      .subscribe((upcoming) => {
        this._isNext = upcoming && upcoming.id === this._lesson?.id;
        this.prepareProgress();
      });
  }

  prepareProgress(): void {
    if(!this._lesson) return;
    this.nativeServiceApiProvider
      .colV3()
      .pipe(
        switchMap((api) => api.getLessonProgress(this._lesson.id)),
        map((progressDetails) => {
          const progress: LessonsV3.CallanProgressDetails = {
            start: progressDetails?.start ?? 1,
            reading: progressDetails?.reading ?? 1,
            nwp: progressDetails?.nwp ?? 1,
            headwords: progressDetails?.headwords ?? null,
            productVersion: progressDetails?.productVersion ?? null
          };
          return progress;
        })
      )
      .subscribe((progress) => {
        progress = Utils.jsonClone(progress);
        progress.start -= this.REVISION_LESSONS;
        if (progress.start < 1) {
          progress.start = 1;
        }
        this.progress = progress;
      });
  }

  public getLessonTeacherPhotoUrl() {
    return this.lessonTeacherPhotoUrl;
  }

  isStudentNoShow() {
    if (!this._lesson) {
      return false;
    }
    return (
      this.lessonFlags &&
      this.lessonFlags.some(
        (f) => f.flagType === LessonsV3.LessonFlagType.StudentNotShow
      )
    );
  }

  isTutorNoShow() {
    if (!this._lesson) {
      return false;
    }
    return (
      this.lessonFlags &&
      this.lessonFlags.some(
        (f) => f.flagType === LessonsV3.LessonFlagType.TeacherNotShow
      )
    );
  }

  isLessonStatus(...expectedStatuses: LessonsV3.LessonStatus[]): boolean {
    if (!this._lesson || !expectedStatuses) {
      return false;
    }
    return expectedStatuses.some((es) => es === this._lesson.status);
  }

  isInvalidated() {
    return this.isLessonStatus(
      LessonsV3.LessonStatus.InvalidatedByStudent,
      LessonsV3.LessonStatus.InvalidatedByTeacher
    );
  }

  isCancelled() {
    return this.isLessonStatus(LessonsV3.LessonStatus.Cancel);
  }

  isFinished() {
    return this.isInvalidated() || this.isCancelled() || this.isComplete();
  }

  isInProgress() {
    return this.isLessonStatus(LessonsV3.LessonStatus.InProgress);
  }

  isByStudent() {
    if (!this.lessonCancellationDetails) {
      return false;
    }
    return !this.lessonCancellationDetails.cancelledByTeacher;
  }

  isClose() {
    if (!this._lesson) {
      return false;
    }
    const timeToTheLesson = Dates.diff(
      new Date(),
      this._lesson.lessonMetric.plannedStartDate
    );
    return !(timeToTheLesson === 0 ||
      timeToTheLesson > TimeUnits.Hours(12).toMilis());

  }

  isFuture() {
    if (!this._lesson) {
      return false;
    }

    const timeToTheLesson = Dates.diff(
      new Date(),
      this._lesson.lessonMetric.plannedStartDate
    );
    return timeToTheLesson >= 0;

  }

  isComplete() {
    return this.isLessonStatus(LessonsV3.LessonStatus.Complete);
  }

  isNext() {
    return this._isNext;
  }

  hasCancellationDetails() {
    return this.lessonCancellationDetails;

  }

  getMessage() {
    return this.lessonMessage?.message ?? '';
  }

  getCancellationReason() {
    if (!this.lessonCancellationDetails) {
      return '';
    }
    return this.lessonCancellationDetails.cancellationReason;
  }

  getStatusDescription() {
    if (!this._lesson) {
      return '';
    }
    const status = this._lesson.status;
    let result: string = status;
    if (status === LessonsV3.LessonStatus.Complete && this.isStudentNoShow()) {
      result = 'Student_No_Show';
    }
    return 'COL.LESSONS.DETAILS.STATUS_DESCRIPTIONS.' + result.toUpperCase();
  }

  public getRecommendedReading() {
    if (!this.progress || !this.progress.nwp) {
      return 1;
    }
    return Math.max(this.progress.nwp - this.READING_RECOMENDATION, 1);
  }

  prepareDetails(): void {
    this.startedAt = null;
    this.plannedAt = null;
    this.isComing = false;
    this.isStarted = false;
    this.shouldStart = false;
    this.mayStart = false;
    this.prepareRoomInfo();
    if (!this._lesson) {return; }
    const metric = this._lesson.lessonMetric;
    if (metric?.started) {
      this.startedAt = new Date(metric.started);
    }
    const lessonStatus = this._lesson.status;
    this.plannedAt = metric?.plannedStartDate ? new Date(metric.plannedStartDate) : null;
    this.isComing =
      lessonStatus === LessonsV3.LessonStatus.Booked ||
      lessonStatus === LessonsV3.LessonStatus.Due;
    this.isStarted = lessonStatus === LessonsV3.LessonStatus.InProgress;
    this.shouldStart = this.isComing && Dates.diff(this.plannedAt, new Date()) > TimeUnits.Minutes(3).toMilis();
    this.isAfterPlannedStartDate = this.isComing && Dates.diff(this.plannedAt, new Date()) > 0;
    this.mayStart = this.isComing && Dates.diff(new Date(), this.plannedAt) < TimeUnits.Minutes(5).toMilis();
    const cancelableStatuses: LessonsV3.LessonStatus[] = [
      LessonsV3.LessonStatus.Booked,
      LessonsV3.LessonStatus.Initializing,
      LessonsV3.LessonStatus.Due,
    ];
    this.cancelable =
      cancelableStatuses.includes(lessonStatus) &&
      !this.shouldStart &&
      !this.mayStart &&
      !this.isClose();
  }

  prepareRoomInfo(): void {
    this.roomUrl = null;
    this.isSkype = false;
    if (!this.lessonRoomUrlRaw) {return; }
    if (this.lessonRoomUrlRaw.startsWith('skype:')) {
      this.isSkype = true;
      const url = this.lessonRoomUrlRaw + '?call';
      this.roomUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } else if (this.lessonRoomUrlRaw.startsWith('video_room_url:')) {
      const url = this.lessonRoomUrlRaw.substring('video_room_url:'.length);
      this.roomUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
  }

  prepareTimePhrase(): string {
    if (!this._lesson) {return ''; }
    const timeReference = this.isStarted ? this.startedAt : this.plannedAt;
    return this.toTimeStr(Dates.diffAbs(timeReference, new Date()));
  }

  toTimeStr(time: number): string {
    return Dates.toTimeStr(time);
  }

  isLessonGoingToStartRegularly(): boolean {
    return this.isComing && !this.shouldStart && !this.isAfterPlannedStartDate && !(this.roomUrl && this.mayStart);
  }

  getTeacherSkype() {
    return this.teacherSkype;
  }

  getTeacherSkypeUrl() {
    if (!this.teacherSkype) return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(`skype:${this.teacherSkype}`);
  }

  mayEnterClassroom(): boolean {
    return this.roomUrl && (this.mayStart || this.shouldStart || this.isStarted);
  }

  isTeacherMissingToStartLesson(): boolean {
    return this.isAfterPlannedStartDate && !this.roomUrl;
  }

  mayEnterRoomBySkype(): boolean {
    return this.mayEnterClassroom() && this.isSkype;
  }

  mayEnterVideoRoom(): boolean {
    return this.mayEnterClassroom() && !this.isSkype;
  }

  getRoomUrl(): SafeResourceUrl {
    return this.roomUrl;
  }

  moveToInstructions() {
    this.router.navigate(['student', this._studentId, 'tutorials', 'prova-booked'],
      {queryParams: {back: '/'}}).then();
  }

  cancelLesson(): void {
    if (!this._lesson) {
      this.cancelLessonModal.hide();
      return;
    }

    let cancellationObservable: Observable<LessonsV3.LessonView | void> | null = null;
    if (
      (this._lesson.status === LessonsV3.LessonStatus.Booked ||
        this._lesson.status === LessonsV3.LessonStatus.Initializing) &&
      !this.mayStart
    ) {
      cancellationObservable = this.nativeServiceApiProvider.colV3().pipe(
        switchMap((api) => api.updateLessonStatus(this._lesson.id, LessonsV3.LessonStatusAction.Cancel, { reason: this.cancelReason }, this._studentId))
      );
    } else if (
      this._lesson.status === LessonsV3.LessonStatus.Due ||
      this._lesson.status === LessonsV3.LessonStatus.Booked
    ) {
      cancellationObservable = this.nativeServiceApiProvider.colV3().pipe(
        switchMap((api) => api.updateLessonStatus(this._lesson.id, LessonsV3.LessonStatusAction.Invalidate, { reason: this.cancelReason }, this._studentId))
      );
    }

    if (cancellationObservable) {
      cancellationObservable
        .pipe(tap(() => this.loadLessonDetails()))
        .subscribe();
    }

    this.cancelLessonModal.hide();
  }


  getTranslations(): void {
    const currentLang = this.translateService.currentLang;
    if (currentLang === undefined) return;

    this.translateService.get('COL.LESSONS.HOW_TO_JOIN_LESSON.MODAL.BODY').subscribe(
      (value) => this.htmlContent = this.sanitizer.bypassSecurityTrustHtml(value)
    )
    this.translateService.get('BUTTON.COPY_LINK').subscribe(
      (value) => this.tooltipCopyText = value
    )
    this.translateService.get('LESSONS.ROOM_DETAILS.LINK_COPIED').subscribe(
      (value) => this.tooltipCopiedText = value
    )
  }

  copyToClipboard() {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = this.lessonRoomUrlRaw?.substring('video_room_url:'.length) ?? ''
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
    this.isCopied = true

    setTimeout(() => {
      this.isCopied = false;
    }, this.TOOLTIP_TIME);
  }

  getMobileCopyButtonText() {
    return this.isCopied ? 'LESSONS.ROOM_DETAILS.LINK_COPIED' : 'LESSONS.ROOM_DETAILS.OR_COPY_LINK'
  }

  prepareTooltips() {
    const copyTooltipEl = document.getElementById('copyTooltip');
    const lessonNotStartedTooltipEl = document.getElementById('lessonNotStartedTooltip');

    if (copyTooltipEl) {
      const copyTooltip = new bootstrap.Tooltip(copyTooltipEl);
      copyTooltipEl.addEventListener('mouseenter', () => {
        this.setTooltipTitle(copyTooltipEl);
        copyTooltip.show();
      })

      copyTooltipEl.addEventListener('click', () => {
        this.setTooltipTitle(copyTooltipEl);
        copyTooltip.show();
        setTimeout(() => {
          copyTooltip.hide();
        }, this.TOOLTIP_TIME);
      })

      copyTooltipEl.addEventListener('mouseleave', () => {
        if (!this.isCopied) copyTooltip.hide();
      })
    }
    else if (lessonNotStartedTooltipEl) {
      const lessonNotStartedTooltip = new bootstrap.Tooltip(lessonNotStartedTooltipEl);

      lessonNotStartedTooltipEl.addEventListener('mouseenter', () => {
        lessonNotStartedTooltip.show();
      })
      lessonNotStartedTooltipEl.addEventListener('mouseleave', () => {
        lessonNotStartedTooltip.hide();
      })
    }
    else return

    this.isTooltipPrepared = true
  }

  setTooltipTitle(tooltipEl: HTMLElement) {
    tooltipEl.setAttribute('data-bs-original-title', this.isCopied ? this.tooltipCopiedText : this.tooltipCopyText);
    if (this.isCopied) {
      setTimeout(() => {
        tooltipEl.setAttribute('data-bs-original-title', this.tooltipCopyText);
      }, this.TOOLTIP_TIME);
    }
  }

  getProvaVideoUrl(): void {
    let currentLang = this.translateService.currentLang?.toUpperCase();
    currentLang = currentLang ? currentLang : 'EN';
    this.provaVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      environment.cloudFrontAssetsEndpoint +
      environment.tutorialsAwsPath +
      "Intro%20video%20subs%20" + currentLang + ".webm");
  }

  private getLessonCourseCode(lesson: LessonsV3.LFStudentGeneral | null = this._lesson): string | null {
    if (!lesson || !('course' in lesson) || !lesson.course) {
      return null;
    }
    return lesson.course.code ?? null;
  }

  private getProductCodeFromCourseCode(courseCode: string | null): string | null {
    if (!courseCode) {
      return null;
    }
    return CourseProductMapper.toProductCode(courseCode) || null;
  }

  private getLessonTeacherDetails(lesson: LessonsV3.LFStudentGeneral): LessonsV3.Teacher | null {
    if (!lesson.teacher?.reference || !('person' in lesson.teacher.reference)) {
      return null;
    }
    return lesson.teacher.reference as LessonsV3.Teacher;
  }

  private getLessonCancellationDetails(lesson: LessonsV3.LFStudentGeneral): LessonsV3.ApiLessonCancellationDetails | null {
    return lesson.cancellationDetails ?? null;
  }

  private getLessonRoomUrl(lesson: LessonsV3.LFStudentGeneral): string | null {
    return lesson.roomUrl ?? null;
  }
  private loadLessonTeacherPhoto(): void {
    const personId = this.lessonTeacher?.person?.id;
    if (!personId) {
      this.lessonTeacherPhotoUrl = null;
      return;
    }
    this.nativeServiceApiProvider.casa().pipe(
      switchMap((api) => api.getPhoto(personId)),
      map((photo) => photo?.publicAccessUrl ?? null),
      catchError(() => of(null))
    ).subscribe((photoUrl) => {
      this.lessonTeacherPhotoUrl = photoUrl;
    });
  }
}
